import { t, tf } from "./i18n.js";
import { createDartboard } from "./dartboard.js";

// ===========================================================================
// BULL-OFF (15.08.2026)
//
// Wer naeher am Bull steckt, faengt an. Ein Dart je Spieler, gemessen wird
// der Abstand zur Mitte in Millimetern.
//
// WARUM MILLIMETER UND NICHT "BULLSEYE SCHLAEGT BULL":
// Die grobe Regel entscheidet die meisten Faelle gar nicht - zwei Darts im
// aeusseren Bull waeren unentschieden, obwohl einer sichtbar naeher steckt.
// Der Tracker liefert die Koordinaten ohnehin; sie sind der Grund, warum
// das ganze Projekt in Millimetern rechnet. Und die Handeingabe ueber die
// echte Scheibe liefert dieselben Koordinaten (createDartboard gibt xMm/yMm
// aus dem Klick zurueck) - Hand und Tracker werden also nach demselben
// Massstab verglichen, nicht nach zweierlei.
//
// WAS DIESES MODUL NICHT TUT:
// Es kennt keinen Spielmodus. Es bekommt Namen, sammelt je einen Dart und
// meldet die Reihenfolge zurueck. Wer damit was anfaengt - localCurrentTurn,
// cricketState.currentPlayer oder ein current_turn in Supabase - entscheidet
// der Modus. Dieselbe Trennung wie beim Aufnahme-Baustein.
// ===========================================================================

const ID = "bulloff-overlay";

/** Abstand zur Boardmitte in Millimetern. Kleiner ist besser.
 *  Ohne brauchbare Koordinaten: Unendlich - so ein Dart verliert immer,
 *  statt durch eine 0 zufaellig zu gewinnen. */
export function bullAbstand(dart) {
  const x = Number(dart?.xMm ?? dart?.heat?.x_mm ?? dart?.x_mm);
  const y = Number(dart?.yMm ?? dart?.heat?.y_mm ?? dart?.y_mm);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return Infinity;
  return Math.hypot(x, y);
}

/** Aus den Wuerfen die Reihenfolge ableiten.
 *
 *  Rueckgabe: { entschieden, reihenfolge, gleichstand }
 *   - reihenfolge: Spielerindizes, bester zuerst
 *   - gleichstand: die Indizes, die neu werfen muessen (leer, wenn klar)
 *
 *  Eigene Funktion, weil sie die einzige Stelle mit einer Regel ist - und
 *  weil sie sich ohne Browser pruefen laesst. */
export function werteBullOff(abstaende) {
  const gueltig = abstaende.map((d, i) => ({ i, d: Number(d) }));
  const bester = Math.min(...gueltig.map((e) => e.d));

  // Unendlich fuer alle heisst: niemand hat brauchbar geworfen. Dann ist
  // nichts entschieden - alle noch einmal.
  if (!Number.isFinite(bester)) {
    return { entschieden: false, reihenfolge: [], gleichstand: gueltig.map((e) => e.i) };
  }

  const vorne = gueltig.filter((e) => e.d === bester);
  if (vorne.length > 1) {
    return { entschieden: false, reihenfolge: [], gleichstand: vorne.map((e) => e.i) };
  }

  const reihenfolge = [...gueltig].sort((a, b) => a.d - b.d).map((e) => e.i);
  return { entschieden: true, reihenfolge, gleichstand: [] };
}

// ---------------------------------------------------------------------------

let zustand = null;

function el(id) {
  return document.getElementById(id);
}

function baueOverlay() {
  let overlay = el(ID);
  if (overlay) return overlay;

  overlay = document.createElement("div");
  overlay.id = ID;
  overlay.className = "bulloff";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.innerHTML = `
    <div class="bulloff-karte">
      <h2 class="bulloff-titel" id="${ID}-titel"></h2>
      <p class="bulloff-sub" id="${ID}-sub"></p>
      <div class="bulloff-liste" id="${ID}-liste"></div>
      <div class="bulloff-board" id="${ID}-board"></div>
      <div class="bulloff-aktionen">
        <button type="button" class="btn-glass" id="${ID}-abbrechen"></button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  return overlay;
}

function zeichne() {
  if (!zustand) return;
  const { spieler, wuerfe, dran, wartetAuf } = zustand;

  el(`${ID}-titel`).textContent = t("bulloff.titel", "Bull-Off");
  el(`${ID}-sub`).textContent = wartetAuf
    ? tf("bulloff.wartet", { p1: wartetAuf })
    : dran === null
      ? t("bulloff.fertig", "Entschieden")
      : tf("bulloff.dran", { p1: spieler[dran] });

  el(`${ID}-liste`).innerHTML = spieler
    .map((name, i) => {
      const w = wuerfe[i];
      const abstand = w ? bullAbstand(w) : null;
      const text =
        abstand === null
          ? "–"
          : Number.isFinite(abstand)
            ? `${abstand.toFixed(1)} mm`
            : t("bulloff.daneben", "daneben");
      return (
        `<div class="bulloff-zeile${i === dran ? " is-dran" : ""}">` +
        `<span class="bulloff-name"></span>` +
        `<span class="bulloff-wert">${text}</span></div>`
      );
    })
    .join("");
  // Namen sind Nutzereingaben - als Text setzen, nicht als HTML.
  [...el(`${ID}-liste`).querySelectorAll(".bulloff-name")].forEach((n, i) => {
    n.textContent = spieler[i];
  });

  el(`${ID}-abbrechen`).textContent = t("bulloff.abbrechen", "Ohne Bull-Off starten");
}

/** Einen Dart entgegennehmen - von der Scheibe oder vom Tracker. */
function nimmDart(dart) {
  if (!zustand || zustand.dran === null || zustand.wartetAuf) return;

  zustand.wuerfe[zustand.dran] = dart;
  if (zustand.board) {
    const x = Number(dart.xMm ?? dart.heat?.x_mm);
    const y = Number(dart.yMm ?? dart.heat?.y_mm);
    if (Number.isFinite(x) && Number.isFinite(y)) {
      zustand.board.mark(x, y, zustand.spieler[zustand.dran], dart.quelle || "hand");
    }
  }

  if (typeof zustand.onWurf === "function") {
    zustand.onWurf(zustand.dran, bullAbstand(dart));
  }

  naechster();
}

/** Wer ist als Naechstes dran - und ist ueberhaupt schon alles da?
 *
 *  Zwei getrennte Fragen, und der Unterschied ist der ganze Online-Fall:
 *  `reihe` sind die Wuerfe, die AN DIESEM GERAET faellig sind; gewertet wird
 *  aber erst, wenn ALLE Spieler geworfen haben. Wuerde hier nur `reihe`
 *  gepruegt, entschiede der Online-Bull-Off, sobald man selbst geworfen hat -
 *  gegen einen Gegner mit Abstand "unendlich". */
function naechster() {
  const offen = zustand.reihe.filter((i) => !zustand.wuerfe[i]);
  if (offen.length > 0) {
    zustand.dran = offen[0];
    zeichne();
    // Damit ein Bot (oder was sonst nicht selbst tippt) seinen Dart werfen
    // kann, wenn er dran ist - und NUR dann. Wuerde der Modus ihn einfach
    // vorab einwerfen, landete er beim Menschen im Slot.
    if (typeof zustand.onDran === "function") zustand.onDran(zustand.dran);
    return;
  }

  zustand.dran = null;
  const alleDa = zustand.spieler.every((_, i) => zustand.wuerfe[i]);
  if (!alleDa) {
    // Online: der eigene Dart steckt, der des Gegners fehlt noch.
    const fehlt = zustand.spieler.findIndex((_, i) => !zustand.wuerfe[i]);
    zustand.wartetAuf = zustand.spieler[fehlt];
    zeichne();
    return;
  }
  zeichne();
  pruefeErgebnis();
}

function pruefeErgebnis() {
  if (!zustand.spieler.every((_, i) => zustand.wuerfe[i])) return;

  const abstaende = zustand.spieler.map((_, i) => bullAbstand(zustand.wuerfe[i]));
  const ergebnis = werteBullOff(abstaende);

  if (!ergebnis.entschieden) {
    // Gleichstand: nur die Betroffenen werfen neu. Die Scheibe wird
    // geleert, damit die alten Marken nicht mit den neuen verwechselt
    // werden - das Ergebnis der Runde steht ja in der Liste.
    if (typeof showToast === "function") {
      showToast(t("bulloff.gleichstand", "Gleichstand – noch einmal!"), "info");
    }
    setTimeout(() => {
      if (!zustand) return;
      ergebnis.gleichstand.forEach((i) => { zustand.wuerfe[i] = null; });
      // Neu werfen darf man nur, was einem gehoert: online wirft der Gegner
      // an SEINEM Board und meldet sich ueber die Leitung.
      zustand.reihe = ergebnis.gleichstand.filter((i) => zustand.meine.includes(i));
      zustand.wartetAuf = null;
      if (zustand.board) zustand.board.clear();
      naechster();
    }, 900);
    return;
  }

  const fertig = zustand.onFertig;
  const reihenfolge = ergebnis.reihenfolge;
  if (typeof showToast === "function") {
    showToast(tf("bulloff.beginnt", { p1: zustand.spieler[reihenfolge[0]] }), "success");
  }
  setTimeout(() => {
    schliesse();
    if (typeof fertig === "function") fertig(reihenfolge);
  }, 900);
}

function schliesse() {
  const overlay = el(ID);
  if (overlay) overlay.style.display = "none";
  if (zustand) {
    // Der Modus bekommt seinen Adapter zurueck. Ohne diese Zeile bliebe der
    // Tracker beim Bull-Off haengen und der eigentliche Spielmodus wuerde
    // nie wieder beliefert.
    window.currentAppMode = zustand.vorherigerModus;
    delete window.autoScoreAdapter.bulloff;
    window.autoScore?.reset?.();
    window.autoScore?.render?.();
  }
  zustand = null;
}

/**
 * Bull-Off starten.
 *
 * @param {object} conf
 * @param {string[]} conf.spieler      Namen in Ausgangsreihenfolge
 * @param {number[]} [conf.werfen]     Wer wirft (Indizes). Vorgabe: alle.
 *                                     Online wirft jeder Client nur selbst.
 * @param {Function} conf.onFertig     (reihenfolge) => void, bester zuerst
 * @param {Function} [conf.onWurf]     (index, abstandMm) => void, fuer Online
 * @param {Function} [conf.onDran]     (index) => void, sobald jemand dran ist
 * @param {Function} [conf.onAbbruch]  ohne Bull-Off weiterspielen
 * @param {string}   [conf.wartetAuf]  Name, auf den gewartet wird (Online)
 */
export function starteBullOff(conf) {
  const overlay = baueOverlay();
  const vorherigerModus = window.currentAppMode;

  const meine = Array.isArray(conf.werfen)
    ? conf.werfen.slice()
    : conf.spieler.map((_, i) => i);

  zustand = {
    spieler: conf.spieler.slice(),
    wuerfe: conf.spieler.map(() => null),
    // `meine` bleibt konstant (wer an DIESEM Geraet wirft), `reihe` schrumpft
    // im Lauf der Runde und wird beim Gleichstand neu gefuellt.
    meine,
    reihe: meine.slice(),
    dran: null,
    wartetAuf: conf.wartetAuf || null,
    board: null,
    onFertig: conf.onFertig,
    onWurf: conf.onWurf,
    onDran: conf.onDran,
    onAbbruch: conf.onAbbruch,
    vorherigerModus,
  };

  overlay.style.display = "flex";

  // ERST sichtbar, DANN zeichnen - createDartboard misst beim Rendern.
  const behaelter = el(`${ID}-board`);
  behaelter.innerHTML = "";
  zustand.board = createDartboard(behaelter, {
    interactive: true,
    onThrow: (treffer) => nimmDart(treffer),
  });

  el(`${ID}-abbrechen`).onclick = () => {
    const abbruch = zustand?.onAbbruch;
    schliesse();
    if (typeof abbruch === "function") abbruch();
  };

  // Der Tracker zaehlt hier genauso wie im Spiel. Solange das Overlay offen
  // ist, IST der Bull-Off der Modus - deshalb currentAppMode umbiegen statt
  // dem Spielmodus einen Sonderfall einzubauen.
  window.currentAppMode = "bulloff";
  window.autoScoreAdapter = window.autoScoreAdapter || {};
  window.autoScoreAdapter.bulloff = {
    korrigierbar: false,
    // Der Autoscore-Kasten schrumpft damit auf seine Statuszeile. Seine drei
    // Dart-Kacheln und die Summe "Aufnahme 0" waeren hier sinnlos: Der
    // Bull-Off ist EIN Dart, keine Aufnahme. Die Statuszeile bleibt, weil
    // sie die einzige nuetzliche Auskunft gibt - zaehlt der Tracker mit?
    eigeneSlots: true,
    aktiv: () => Boolean(zustand),
    ziel: () => el(`${ID}-board`),
    dart: (dart) => {
      nimmDart({ ...dart, quelle: "erkannt" });
      // Ein Dart, eine Entscheidung: sofort abschliessen, damit autoscore
      // nicht auf zwei weitere Pfeile wartet.
      return { abschliessen: true };
    },
    aufnahme: () => true,
    live: (darts) => {
      if (zustand?.board?.setLive) zustand.board.setLive(darts);
    },
    geleert: () => {
      if (zustand?.board?.setLive) zustand.board.setLive([]);
    },
  };
  window.autoScore?.render?.();

  naechster();
  return {
    /** Wurf des Gegners aus dem Netz (Online).
     *
     *  Uebertragen wird NUR der Abstand, nicht die Koordinaten: Mehr braucht
     *  der Vergleich nicht, und der genaue Einschlag des Gegners geht
     *  niemanden etwas an. Er wird hier als (x = Abstand, y = 0) abgelegt,
     *  damit bullAbstand() ihn ohne Sonderfall wieder herausrechnet - die
     *  Marke auf der eigenen Scheibe unterbleibt entsprechend. */
    fremderWurf(index, abstandMm) {
      if (!zustand) return;
      const d = Number(abstandMm);
      zustand.wuerfe[index] = {
        xMm: Number.isFinite(d) ? d : Infinity,
        yMm: 0,
        quelle: "fern",
      };
      zustand.wartetAuf = null;
      naechster();
    },
    setzeWartet(name) {
      if (!zustand) return;
      zustand.wartetAuf = name || null;
      zeichne();
    },
    offen: () => Boolean(zustand),
    schliesse,
  };
}

Object.assign(window, { starteBullOff, werteBullOff, bullAbstand });
